import { useState, useCallback, useContext } from 'react';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import fetchJson from '@atlassian/jira-fetch/src/utils/as-json.tsx';
import {
	fireOperationalAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { fg } from '@atlassian/jira-feature-gating';
import { issueOperationsCacheContext } from '../utils/state-cache.tsx';
import type { DataState, MediaUploadPermissionResponse, UseUploadPermissions } from './types.tsx';

export const useUploadPermissions: UseUploadPermissions = () => {
	let uploadPermissions: DataState;
	let setUploadPermissions: React.Dispatch<React.SetStateAction<DataState>>;
	if (fg('empanada_nin_convert_issue_ops_to_entrypoint')) {
		// eslint-disable-next-line  react-hooks/rules-of-hooks
		const { mediaUploadPermissions } = useContext(issueOperationsCacheContext);
		[uploadPermissions, setUploadPermissions] = mediaUploadPermissions;
	} else {
		// eslint-disable-next-line  react-hooks/rules-of-hooks
		[uploadPermissions, setUploadPermissions] = useState<DataState>({
			loading: false,
			error: false,
			permissions: null,
		});
	}

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const fetchUploadPermissions = useCallback(
		async (issueId: number): Promise<void> => {
			setUploadPermissions((currentState) => ({
				...currentState,
				loading: true,
			}));

			try {
				const uploadResponse: MediaUploadPermissionResponse = await fetchJson(
					`/rest/api/2/attachment/upload/issue/${issueId}/credentials`,
				);
				setUploadPermissions({
					loading: false,
					error: false,
					permissions: {
						clientId: uploadResponse.clientId,
						serviceHost: uploadResponse.endpointUrl,
						token: uploadResponse.token,
						tokenLifespanInMs: uploadResponse.tokenDurationInMins * 60 * 1000,
						collection: uploadResponse.targetCollection,
						tokenIssueTimestamp: Date.now() - 10000,
					},
				});
				fireOperationalAnalytics(
					createAnalyticsEvent({
						type: 'sendOperationalEvent',
						action: 'success',
						actionSubject: 'fetchUploadPermissions',
					}),
					'fetchUploadPermissions success',
				);
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (e: any) {
				fireErrorAnalytics({
					error: e,
					meta: {
						id: 'fetchUploadPermissions',
						packageName: 'jiraIssueNavigatorIssueOperationsServices',
						teamName: 'empanada',
					},
				});
				setUploadPermissions({
					permissions: null,
					loading: false,
					error: true,
				});
			}
		},
		[createAnalyticsEvent, setUploadPermissions],
	);
	return { uploadPermissions, fetchUploadPermissions };
};

export default useUploadPermissions;
